import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { IntlProps, addTranslation } from 'decorators/addTranslation';
import { WithRouterProps } from 'decorators/withRouter';
import Mapper from 'helpers/Mapper';
import dataMapper from './operationItemDataMapper';
import { AnyObject } from 'types/Common';
import Checkbox from '../../ui/checkbox';
import ConditionalWrapper from '../../ui/conditionalWrapper';
import CopyToClipboard from '../../ui/copyToClipboard';
import LabelStatus from 'components/ui/labelStatus';
import { formatLocalizedDate } from 'helpers/formatLocalizedDate';
import { WL_ID_19 } from 'constants/wlIds';

interface OwnProps {
  data: AnyObject;
  isCurrent: boolean;
  onSelect: (operationId: string) => void;
  onCheckboxChange?: (id: string, isSelected: boolean) => void;
  onStatusClick?: (parentOperationId: string) => void;
  customClass?: string;
  isDisabled?: boolean;
  isCheckboxDisabled?: boolean;
  type?: 'chargeback' | 'operation';
  wlId?: number | null
}

type Props = OwnProps & IntlProps & WithRouterProps;

class OperationItem extends PureComponent<Props> {
  static defaultProps = {
    type: 'operation' as const,
  };

  render() {
    const {
      data,
      isCurrent,
      onSelect,
      type,
      isDisabled,
      isCheckboxDisabled,
      customClass,
      onCheckboxChange,
      onStatusClick,
      wlId
    } = this.props;
    const {
      id,
      label,
      value,
      isSelected,
      operationType,
      updatedDate,
      amount,
      chargeback,
      currency,
      fraud,
      status,
      recurring,
    } = dataMapper(data, type);
    const isChargeback = type === 'chargeback';
    const UPDATED_DATE_FORMAT = wlId === WL_ID_19 ? formatLocalizedDate(updatedDate) : updatedDate;
    return (
      <div
        className={classNames(
          'payment-card__operation-item border-border-base cursor-pointer border-b px-5 py-4 last:border-none',
          {
            'payment-card__operation-item--current bg-background-secondary after:bg-brand-main relative after:absolute after:top-0 after:left-0 after:h-full after:w-1':
              isCurrent,
            'payment-card__operation-item--disabled cursor-default opacity-60':
              isDisabled,
            'payment-card__operation-item--chargeback flex items-center gap-2':
              isChargeback,
            'eto-operation-card': wlId === WL_ID_19
          },
          customClass
        )}
        id={`operation-item-${id}`}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(id);
        }}>
        {isChargeback && (
          <Checkbox
            id={value}
            checked={isSelected}
            disabled={isCheckboxDisabled}
            onChange={(e) => {
              e.persist();
              e.nativeEvent.stopImmediatePropagation();
              e.stopPropagation();
              onCheckboxChange?.(id, e.target.checked);
            }}
          />
        )}
        <ConditionalWrapper
          condition={isChargeback}
          wrap={(children) => <div className='min-w-0'>{children}</div>}>
          {!isChargeback && (
            <div className={classNames('payment-card__operation-item__header', 'mb-2 flex flex-wrap items-center justify-between gap-2')}>
              <span className={classNames('payment-card__operation-item__type', 'text-m-semi-bold capitalize')}>
                {operationType}
              </span>
              <span className={classNames('payment-card__operation-item__date', 'text-text-secondary')}>
                {UPDATED_DATE_FORMAT}
              </span>
            </div>
          )}
          <div className={classNames('payment-card__operation-item__amount', 'font-heading text-heading-sm mb-2 flex gap-1')}>
            <span className='payment-card__operation-item__amount-value'>{amount}</span>
            <span className='payment-card__operation-item__amount-currency'>{currency}</span>
          </div>
          <div className={classNames('payment-card__operation-item__statuses', 'mb-2 flex gap-2')}>
            <LabelStatus customClass='payment-card__operation-item__status' text={status} status={Mapper.getStatus(status)} />

            {recurring ? (
              <LabelStatus
                customClass='payment-card__operation-item__status'
                text={recurring}
                status={Mapper.getStatus(recurring)}
                onClick={(e) => {
                  e.stopPropagation();

                  if (recurring === 'retry operation') {
                    return;
                  }

                  if (onStatusClick) {
                    onStatusClick(id);
                  }
                }}
              />
            ) : null}

            {Boolean(fraud) && (
              <LabelStatus customClass='payment-card__operation-item__status' text='fraud' status={Mapper.getStatus('fraud')} />
            )}
            {Boolean(chargeback) && (
              <LabelStatus
                customClass='payment-card__operation-item__status'
                text='chargeback'
                status={Mapper.getStatus('chargeback')}
              />
            )}
          </div>
          <div className={classNames('payment-card__operation-item__meta', 'flex items-center gap-1')}>
            <span className='payment-card__operation-item__meta-label'>{label}:</span>
            <span className={classNames('payment-card__operation-item__meta-value', 'text-brand-main overflow-hidden text-ellipsis')}>
              {isChargeback ? value : id}
            </span>
            <CopyToClipboard customClass='payment-card__operation-item__copy' text={value} />
          </div>
        </ConditionalWrapper>
      </div>
    );
  }
}

export default withRouter(addTranslation(OperationItem));
